import { Card, Loading, Page, SkeletonBodyText } from "@shopify/polaris";
import React, { useState } from "react";
import styles from "./InspectionImages.module.css";
import ArtifactElementCard from "../ArtifactElementCard";
import ArtifactViewer from "../ArtifactViewer";
import ArtifactGallery from "../ArtifactGallery";
import MainFrameContextType from "../../types/MainFrameContextType";
import InspectionCompleteModal from "../InspectionCompleteModal/InspectionCompleteModal";
import ElementsContextType from "../../types/ElementsContextType";
import { MainFrameContext } from "../contexts/MainFrameContext";
import MarkAsCompleteModal from "../MarkAsCompleteModal/MarkAsCompleteModal";
import MarkAsCompleteHeader from "../MarkAsCompleteHeader/MarkAsCompleteHeader";
import { ElementsContext } from "../contexts/ElementsContext";
import { Bar } from "../Indicators";
import { useDispatch, useSelector } from "react-redux";
import { getInspectionById } from "../../store/actions/Inspections";
import { AssetType } from "../../types/AssetType";
import { PortalState } from "../../types/PortalState";
import SurveyorCompletionStatus from "../../types/SurveyorCompletionStatus";
import { WarningBanner } from "../WarningBanner/WarningBanner";
import DeleteBulkModal from "../DeleteView/DeleteBulkModal";
import {clearDeleteData} from "../../store/actions/Asset";

interface Props {
    id: string;
    elementIndex?: string;
}

export default function InspectionPhotos({ id, elementIndex }: Props) {
    const { setShowSideBar, anchor } = React.useContext(
        MainFrameContext
    ) as MainFrameContextType;
    const {
        elements,
        setElements,
        setFocusedInspectionElement,
        focusedInspectionElement,
        focusedImage,
        showGallery,
        showImageViewer,
        setShowImageViewer,
        setShowGallery,
        setInspection
    } = React.useContext(ElementsContext) as ElementsContextType;
    const [loading, setLoading] = useState(true);

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const activeInspection = useSelector(
        (state: PortalState) => state.portal.activeInspection
    );
    const surveyorCompletionStatus: SurveyorCompletionStatus | undefined =
        useSelector(
            (state: PortalState) =>
                state.portal.activeInspection?.SurveyorCompletionStatus
        );

    const dispatch = useDispatch();

    const [showMarkAsCompleteModal, setShowMarkAsCompleteModal] =
        React.useState(false);

    const handleElementIndexSelection = () => {
        if (elementIndex !== undefined && elements) {
            const focused = elements.find((imageElement) => {
                return elementIndex.includes(imageElement.Id);
            });
            if (focused !== undefined) {
                setFocusedInspectionElement(focused);
                setShowGallery(true);
            }
        } else if (elements && (anchor === "#landing" || anchor === "#")) {
            setShowGallery(false);
            setShowImageViewer(false);
        }
    };

    handleElementIndexSelection();

    React.useEffect(() => {
        dispatch(getInspectionById(id));
    }, []);

    React.useEffect(() => {
        setShowSideBar(true);
    }, []);

    function openModal() {
        setShowMarkAsCompleteModal(true);
    }

    let sectionComplete = false;

    if (!loading && surveyorCompletionStatus !== undefined) {
        sectionComplete = surveyorCompletionStatus.PhotosComplete;
    }

    React.useEffect(() => {
        if (activeInspection !== undefined) {
            setInspection(activeInspection);
            const imageElements = activeInspection.sections.filter(
                (element: any) => {
                    return element.ElementType === 2;
                }
            );
            setElements(imageElements);
            setLoading(false);
        }
    }, [activeInspection]);

    React.useEffect(() => {
        if (anchor === "#landing") {
            setShowGallery(false);
            setShowImageViewer(false);
        }
    }, [anchor]);

    let dueDate = "";
    if (activeInspection !== undefined) {
        dueDate = activeInspection.DueDate;
    }

    return showImageViewer && focusedInspectionElement && focusedImage ? (
        <ArtifactViewer
            type={AssetType.Image}
            focalImage={focusedImage}
            dataId={focusedInspectionElement.Id}
        />
    ) : loading ? (
        <Page fullWidth>
            <div>
                <Loading />

                <Card sectioned>
                    <SkeletonBodyText />
                </Card>

                <Card sectioned>
                    <SkeletonBodyText />
                </Card>
            </div>
        </Page>
    ) : showGallery && focusedInspectionElement ? (
        <div>
            <Page title={focusedInspectionElement?.Name} fullWidth>
                <article>
                    {[...elements]
                        .filter((x) => x.Id === focusedInspectionElement.Id)
                        .map((element) => (
                            <ArtifactGallery
                                key={element.Name}
                                data={element}
                            />
                        ))}
                </article>
            </Page>
        </div>
    ) : (
        <>
            <div>
                <DeleteBulkModal
                    active={showDeleteModal}
                    closeModal={() => {
                        setShowDeleteModal(false);
                        dispatch(clearDeleteData());
                    }}
                />

                <MarkAsCompleteModal
                    type={AssetType.Image}
                    active={showMarkAsCompleteModal}
                    closeModal={() => {
                        setShowMarkAsCompleteModal(false);
                    }}
                />

                <InspectionCompleteModal />

                {sectionComplete ? (
                    <div />
                ) : (
                    <div className={styles.completeHeader}>
                        <MarkAsCompleteHeader
                            inspectionEnd={dueDate}
                            openModal={openModal}
                        />
                    </div>
                )}

                <WarningBanner />
                <Page fullWidth>
                    <div
                        className={
                            sectionComplete
                                ? styles.pageHeaderNoGap
                                : styles.pageHeader
                        }
                    >
                        <h1 className={styles.title}>Photos</h1>
                        {sectionComplete ? (
                            <h1>Section Marked as Complete</h1>
                        ) : (
                            <Bar />
                        )}
                    </div>

                    <article className={styles.twoColumnLayout}>
                        {[...elements].map((element) => (
                            <div key={element.Id} className={styles.column}>
                                <ArtifactElementCard
                                    key={element.Name}
                                    assetType={AssetType.Image}
                                    element={element}
                                    deleteDialogCallback={setShowDeleteModal}
                                />
                            </div>
                        ))}
                    </article>
                </Page>
            </div>
        </>
    );
}
